"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Layers, ShieldCheck, Droplets, Gauge, FileText } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const features = [{
  icon: Droplets,
  title: "Distribuição uniforme",
  description: "Espessura de camada controlada ao micrómetro sem desperdício de fluido."
}, {
  icon: ShieldCheck,
  title: "Proteção anticorrosiva",
  description: "Revestimentos estanques e consistentes em chapa, bobinas e perfis."
}, {
  icon: Gauge,
  title: "Eficiência de caudal",
  description: "Sistemas automáticos que ajustam o débito à velocidade de linha."
}];
export default function RoofingHero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="roofingcladdinghero" data-nav="dark" className="relative overflow-hidden bg-primary text-primary-foreground py-20 lg:py-28">
      {/* Background visual elements */}
      <div className="pointer-events-none absolute inset-0 opacity-10">
        <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-secondary blur-3xl" />
        <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-secondary blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
      </div>

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
          {/* Main Hero Copy */}
          <div className="lg:col-span-7">
            <motion.div variants={itemVariants} className="mb-4">
              <Badge variant="outline" className="border-secondary/40 bg-secondary/15 text-primary-foreground px-3 py-1 text-xs font-semibold uppercase tracking-wider">
                <Layers className="size-3.5 text-secondary" />
                Sistemas de Revestimento Industrial
              </Badge>
            </motion.div>

            <motion.h1 variants={itemVariants} className="font-default text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
              Coberturas & Revestimentos
            </motion.h1>

            <motion.p variants={itemVariants} className="mt-6 text-base text-primary-foreground/80 sm:text-lg lg:max-w-xl leading-relaxed">Melhore o desempenho do revestimento com tecnologia de pulverização concebida para cobertura uniforme, taxas de aplicação repetíveis e desperdício mínimo. Independentemente da indústria ou do processo, implemente bicos feitos para aplicar a quantidade certa de revestimento exatamente onde é necessário. Aplicação de precisão para óleos, ceras, resinas e agentes protetores em processos contínuos de fabrico. Maximize a qualidade superficial e reduza perdas de matéria-prima.</motion.p>

            <motion.div variants={itemVariants} className="mt-8 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-md">
                <Link to="/ContactsPage">
                  <span>Pedir proposta técnica</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-primary-foreground/30 bg-transparent text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground">
                <Link to="/CatalogsPage">
                  <FileText className="size-4" />
                  <span>Ver catálogos técnicos</span>
                </Link>
              </Button>
            </motion.div>

            {/* Technical highlight specs */}
            <motion.div variants={itemVariants} className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-3 pt-8 border-t border-primary-foreground/15">
              {features.map((feature, i) => {
              const IconComponent = feature.icon;
              return <div key={feature.title} data-index={i} className="flex flex-col gap-1.5">
                    <div className="flex items-center gap-2">
                      <div className="flex size-7 items-center justify-center rounded-md bg-secondary/20 text-secondary-foreground">
                        <IconComponent className="size-4" />
                      </div>
                      <h2 className="text-sm font-semibold text-primary-foreground">
                        {feature.title}
                      </h2>
                    </div>
                    <p className="text-xs text-primary-foreground/75 leading-normal">
                      {feature.description}
                    </p>
                  </div>;
            })}
            </motion.div>
          </div>

          {/* Technical Visual Card */}
          <div className="lg:col-span-5">
            <motion.div variants={itemVariants} className="relative overflow-hidden rounded-2xl border border-primary-foreground/15 bg-card text-card-foreground shadow-2xl p-2">
              <div className="relative aspect-4/3 w-full overflow-hidden rounded-xl bg-muted">
                <img data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/industrial_roofing_coating_system.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/industrial_roofing_coating_system.png-wvc-sizes" alt="Sistema de pulverização para revestimentos industriais e coberturas" loading="lazy" decoding="async" className="h-full w-full object-cover" src="https://eurocontrol.pt/wp-content/uploads/2026/09/Captura-de-ecra-2026-09-07-as-15.32.29.png" />
                <div className="absolute inset-0 bg-linear-to-t from-foreground/80 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-background">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-secondary animate-pulse" />
                    <p className="text-xs font-semibold uppercase tracking-wider text-background/90">
                      Spraying Systems Co.
                    </p>
                  </div>
                  <p className="text-sm font-medium text-background mt-0.5">
                    Tecnologia AutoJet® para controlo de dosagem
                  </p>
                </div>
              </div>

              <div className="p-4 sm:p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">
                      Soluções de Engenharia
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Representação exclusiva em Portugal desde 1993
                    </p>
                  </div>
                  <Button asChild size="sm" variant="secondary">
                    <Link to="/ProductsPage">
                      <span>Explorar</span>
                    </Link>
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>;
}